import React, { useState, useMemo, memo } from 'react';
import { ArrowUpDown } from 'lucide-react';
import { ProductDetailModal } from './dashboard/modals/ProductDetailModal';
import type { Product as TycoonProduct } from '@smartphone-tycoon/shared-core';
import { formatCurrency } from '@smartphone-tycoon/shared-ui';
import { formatCompactNumber } from '@smartphone-tycoon/shared-core';

interface Product {
  id: string;
  companyName: string;
  name: string;
  price: number;
  techScore: number;
  userRating: number;
  marketAppeal: number;
  releaseDate?: {
    day: number;
    month: number;
    year: number;
  };
  discontinuedDate?: {
    day: number;
    month: number;
    year: number;
  };
  status: 'development' | 'production' | 'discontinued';
  unitsSold: number;
  isPlayerProduct: boolean;
  marketShare?: number;
  inventory?: number;
}

interface ProductListingTableProps {
  products: Product[];
  marketShareData?: Product[];
  onProductAction?: (productId: string, action: string) => void;
}

export const ProductListingTable = memo(function ProductListingTable({ products, onProductAction }: ProductListingTableProps) {
  // Diagnostic logging (remove after validation)
  // console.log(`🔍 [ProductListingTable] ${products.length} products (${products.filter(p => !p.isPlayerProduct).length} competitor)`);

  const [sortConfig, setSortConfig] = useState<{
    key: keyof Product;
    direction: 'asc' | 'desc';
  }>({ key: 'unitsSold', direction: 'desc' });

  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [filters, setFilters] = useState({
    search: '',
    showPlayerProducts: true,
    showCompetitorProducts: true,
    showDevelopment: false,
    showProduction: true,
    showDiscontinued: false
  });

  const handleProductClick = (product: Product) => {
    // Convert the product listing format to the full Product format expected by the modal
    const fullProduct: TycoonProduct = {
      id: product.id,
      name: product.name,
      price: product.price || 0,
      techScore: product.techScore || 0,
      marketAppeal: product.marketAppeal || 0,
      status: product.isPlayerProduct ? (product.status || 'production') : 'production',
      description: `${product.name} by ${product.companyName}`,
      developmentCost: product.price * 0.6 || 0,
      productionCost: product.price * 0.4 || 0,
      salesMetrics: {
        unitsSold: product.unitsSold || 0,
        revenue: (product.unitsSold || 0) * (product.price || 0),
        averageRating: product.userRating || 0,
        marketShare: product.marketShare || 0
      },
      metadata: {
        companyId: product.isPlayerProduct ? 'player' : 'competitor',
        designDate: product.releaseDate,
        releaseDate: product.releaseDate,
        discontinuedDate: product.discontinuedDate
      },
      companyName: product.companyName
    };
    setSelectedProduct(fullProduct);
    setIsModalOpen(true);
  };

  const handleModalAction = (productId: string, action: string) => {
    if (onProductAction) {
      onProductAction(productId, action);
    }
    setIsModalOpen(false);
  };

  const handleSort = (key: keyof Product) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredAndSortedProducts = useMemo(() => {
    // Products already include market share data
    const productsWithMarketShare = products;

    const filtered = productsWithMarketShare
      .filter(product => {
        const searchMatch = !filters.search ||
          (product.name && product.name.toLowerCase().includes(filters.search.toLowerCase())) ||
          (product.companyName && product.companyName.toLowerCase().includes(filters.search.toLowerCase()));

        const playerFilterMatch =
          (filters.showPlayerProducts && product.isPlayerProduct) ||
          (filters.showCompetitorProducts && !product.isPlayerProduct);

        // Handle both exact matches and case variations
        const normalizedStatus = product.status?.toLowerCase();
        const statusFilterMatch =
          (filters.showDevelopment && normalizedStatus === 'development' && product.isPlayerProduct) ||
          (filters.showProduction && (normalizedStatus === 'production' || normalizedStatus === 'released' || (!product.isPlayerProduct && ['development', 'manufacturing'].includes(normalizedStatus)))) ||
          (filters.showDiscontinued && normalizedStatus === 'discontinued');

        // Special case: Always consider products with discontinued date as discontinued regardless of status string
        if (product.discontinuedDate && filters.showDiscontinued) {
          const matchesOtherFilters = searchMatch && playerFilterMatch;
          if (matchesOtherFilters) {
            return true;
          }
        }

        return searchMatch && playerFilterMatch && statusFilterMatch;
      });

    // Diagnostic logging (remove after validation)
    // console.log(`🔍 [ProductListingTable] Filtered: ${filtered.length}/${productsWithMarketShare.length} products (${filtered.filter(p => !p.isPlayerProduct).length} competitor)`);

    return filtered
      .sort((a, b) => {
        if (sortConfig.key === 'releaseDate' && a.releaseDate && b.releaseDate) {
          const dateA = new Date(a.releaseDate.year, a.releaseDate.month - 1, a.releaseDate.day);
          const dateB = new Date(b.releaseDate.year, b.releaseDate.month - 1, b.releaseDate.day);
          return sortConfig.direction === 'asc' 
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }
        
        if (sortConfig.key === 'discontinuedDate') {
          const dateA = a.discontinuedDate ? new Date(a.discontinuedDate.year, a.discontinuedDate.month - 1, a.discontinuedDate.day) : null;
          const dateB = b.discontinuedDate ? new Date(b.discontinuedDate.year, b.discontinuedDate.month - 1, b.discontinuedDate.day) : null;
          
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return sortConfig.direction === 'asc' 
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        const valueA = a[sortConfig.key];
        const valueB = b[sortConfig.key];

        if (valueA === valueB) return 0;
        if (valueA === undefined) return 1;
        if (valueB === undefined) return -1;

        const comparison = valueA < valueB ? -1 : 1;
        return sortConfig.direction === 'asc' ? comparison : -comparison;
      });
  }, [products, sortConfig, filters]);

  const formatDate = (date?: { day: number; month: number; year: number }, context: string) => {
    if (context === 'development') {
      return '-';
    }
    
    if (!date) {
      return '-';
    }
    
    const months = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    return `${months[date.month - 1]} ${date.day}, ${date.year}`;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-4 items-center">
        <input
          type="text"
          placeholder="Search products..."
          value={filters.search}
          onChange={e => setFilters(f => ({ ...f, search: e.target.value }))}
          className="px-3 py-2 border border-theme-border rounded-lg text-theme-text-primary bg-theme-bg-secondary"
        />
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showPlayerProducts}
            onChange={e => setFilters(f => ({ ...f, showPlayerProducts: e.target.checked }))}
          />
          Your Products
        </label>
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showCompetitorProducts}
            onChange={e => setFilters(f => ({ ...f, showCompetitorProducts: e.target.checked }))}
          />
          Competitor Products
        </label>
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showDevelopment}
            onChange={e => setFilters(f => ({ ...f, showDevelopment: e.target.checked }))}
          />
          In Development
        </label>
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showProduction}
            onChange={e => setFilters(f => ({ ...f, showProduction: e.target.checked }))}
          />
          Available
        </label>
        <label className="flex items-center gap-2 text-theme-text-primary">
          <input
            type="checkbox"
            checked={filters.showDiscontinued}
            onChange={e => setFilters(f => ({ ...f, showDiscontinued: e.target.checked }))}
          />
          Discontinued
        </label>
      </div>

      <div className="overflow-x-auto -mx-4 px-4 touch-pan-x">
        <table className="w-full border-collapse min-w-[900px]">
          <thead className="bg-theme-bg-secondary">
            <tr>
              {[
                { key: 'companyName', label: 'Company' },
                { key: 'name', label: 'Product' },
                { key: 'price', label: 'Price' },
                { key: 'techScore', label: 'Tech Score' },
                { key: 'userRating', label: 'User Rating' },
                { key: 'marketAppeal', label: 'Market Appeal' },
                { key: 'releaseDate', label: 'Release Date' },
                { key: 'discontinuedDate', label: 'Discontinued Date' },
                { key: 'inventory', label: 'Inventory' },
                { key: 'unitsSold', label: 'Units Sold' },
                { key: 'marketShare', label: 'Market Share %' }
              ].map(({ key, label }) => (
                <th
                  key={key}
                  onClick={() => handleSort(key as keyof Product)}
                  className="px-4 py-2 text-left text-sm font-medium text-theme-text-muted cursor-pointer hover:bg-theme-bg-tertiary"
                >
                  <div className="flex items-center gap-1">
                    {label}
                    <ArrowUpDown className="w-4 h-4" />
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedProducts.map(product => (
              <tr
                key={product.id}
                onClick={() => handleProductClick(product)}
                className={`border-t cursor-pointer transition-colors ${
                  product.isPlayerProduct
                    ? 'bg-theme-accent/10 hover:bg-theme-accent/20'
                    : 'hover:bg-theme-bg-secondary'
                }`}
              >
                <td className="px-4 py-2 text-theme-text-primary">
                  <span>{product.companyName}</span>
                </td>
                <td className="px-4 py-2 font-medium text-theme-text-primary">{product.name}</td>
                <td className="px-4 py-2 text-theme-text-primary">{formatCurrency(product.price)}</td>
                <td className="px-4 py-2 text-theme-text-primary">{product.techScore.toFixed(1)}</td>
                <td className="px-4 py-2 text-theme-text-primary">{product.userRating.toFixed(1)}</td>
                <td className="px-4 py-2 text-theme-text-primary">{product.marketAppeal}%</td>
                <td className="px-4 py-2 text-theme-text-primary">
                  {formatDate(product.releaseDate, product.isPlayerProduct ? product.status : 'production')}
                </td>
                <td className="px-4 py-2 text-theme-text-primary">{formatDate(product.discontinuedDate, 'discontinued')}</td>
                <td className="px-4 py-2 text-theme-text-primary">
                  {product.inventory !== undefined ? formatCompactNumber(product.inventory) : '-'}
                </td>
                <td className="px-4 py-2 text-theme-text-primary">{formatCompactNumber(product.unitsSold)}</td>
                <td className="px-4 py-2">
                  {product.status === 'discontinued' ? (
                    <span className="text-theme-text-muted">-</span>
                  ) : product.marketShare && product.marketShare > 0 ? (
                    <span className={`font-medium ${
                      product.isPlayerProduct ? 'text-theme-info' : 'text-theme-text-secondary'
                    }`}>
                      {product.marketShare.toFixed(1)}%
                    </span>
                  ) : (
                    <span className="text-theme-text-muted">0.0%</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      
      <ProductDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={selectedProduct}
        onAction={handleModalAction}
        isPlayerProduct={selectedProduct?.metadata?.companyId === 'player'}
        companyName={selectedProduct?.companyName}
      />
    </div>
  );
});